import {
  findSubjectLinkMatches,
  type SubjectLinkTarget,
} from './subjectLinkify'

/** URL scheme `SubjectLinkedAgenticMarkdown` recognizes and intercepts. The
 * fragment-first form survives react-markdown v10's default `urlTransform`
 * (it only sanitizes `http(s):`/`mailto:`-shaped absolute URLs). */
export const SUBJECT_LINK_PREFIX = '#subject:'

/**
 * Minimal structural mdast typing — only the shape this plugin reads or
 * writes. Casting the real mdast-util-to-markdown tree through this is safe
 * because every field here is a strict subset of the real node.
 */
interface MdNode {
  type: string
  value?: string
  url?: string
  title?: string | null
  children?: MdNode[]
}

/** Node types whose text content is never eligible for subject linking:
 * code (rendered verbatim), existing links/images (already navigational),
 * raw html, and link/image reference definitions. */
const SKIP_TYPES = new Set([
  'code',
  'inlineCode',
  'link',
  'linkReference',
  'image',
  'imageReference',
  'html',
  'definition',
])

const linkifyTextNode = (
  node: MdNode,
  index: ReadonlyMap<string, SubjectLinkTarget>
): MdNode[] => {
  const text = node.value ?? ''
  const matches = findSubjectLinkMatches(text, index)
  if (matches.length === 0) return [node]

  const out: MdNode[] = []
  let cursor = 0
  for (const match of matches) {
    if (match.start > cursor) {
      out.push({ type: 'text', value: text.slice(cursor, match.start) })
    }
    out.push({
      type: 'link',
      url: `${SUBJECT_LINK_PREFIX}${encodeURIComponent(match.uid)}`,
      title: match.name,
      children: [{ type: 'text', value: text.slice(match.start, match.end) }],
    })
    cursor = match.end
  }
  if (cursor < text.length) {
    out.push({ type: 'text', value: text.slice(cursor) })
  }
  return out
}

const walk = (node: MdNode, index: ReadonlyMap<string, SubjectLinkTarget>) => {
  if (SKIP_TYPES.has(node.type) || !node.children) return
  node.children = node.children.flatMap((child) => {
    if (child.type === 'text') return linkifyTextNode(child, index)
    walk(child, index)
    return [child]
  })
}

/**
 * A hand-rolled remark plugin that turns subject-name/alias mentions in plain
 * text into `#subject:<uid>` links, so doc-chat responses can surface
 * clickable Subject references with zero new dependencies. Only `text`
 * children of non-skipped nodes are considered; code, existing links/images,
 * and raw html are left untouched.
 */
export function remarkSubjectLinks(options: {
  index: ReadonlyMap<string, SubjectLinkTarget>
}) {
  return (tree: MdNode): void => {
    walk(tree, options.index)
  }
}
